import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, FileText, Download, Trash2, Search, X } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const STATUS_COLORS = {
  'Draft': 'bg-gray-500',
  'Sent': 'bg-blue-500',
  'Cancelled': 'bg-red-500',
  'Complete': 'bg-green-500'
};

const PurchaseOrdersPage = () => {
  const [pos, setPOs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [formData, setFormData] = useState({
    supplier_id: '',
    items: [],
    notes: ''
  });
  const [selectedProducts, setSelectedProducts] = useState([]);

  useEffect(() => {
    fetchPOs();
    fetchSuppliers();
    fetchProducts();
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchPOs();
  }, [statusFilter]); // eslint-disable-line

  const fetchPOs = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = (statusFilter && statusFilter.trim() && statusFilter !== 'all') ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/purchase-orders${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPOs(response.data);
    } catch (error) {
      toast.error('Failed to fetch purchase orders');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to fetch suppliers');
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    }
  };

  const addProduct = (product) => {
    const existingItem = selectedProducts.find(item => item.product_id === product.id);
    if (existingItem) {
      toast.error('Product already added');
      return;
    }

    const newItem = {
      product_id: product.id,
      product_name: product.product_name,
      sku: product.sku,
      quantity: 1,
      unit_price: product.unit_price,
      total: product.unit_price
    };
    setSelectedProducts([...selectedProducts, newItem]);
    setProductSearch('');
  };

  const updateItemQuantity = (productId, quantity) => {
    const qty = parseInt(quantity) || 0;
    setSelectedProducts(selectedProducts.map(item => 
      item.product_id === productId
        ? { ...item, quantity: qty, total: qty * item.unit_price }
        : item
    ));
  };

  const removeProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(item => item.product_id !== productId));
  };

  const calculateTotal = () => {
    const subtotal = selectedProducts.reduce((sum, item) => sum + item.total, 0);
    const tax = subtotal * 0.10;
    return { subtotal, tax, total: subtotal + tax };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedProducts.length === 0) {
      toast.error('Please add at least one product');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const submitData = {
        ...formData,
        items: selectedProducts
      };
      
      if (editingPO) {
        await axios.put(`${API}/purchase-orders/${editingPO.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase order updated successfully');
      } else {
        await axios.post(`${API}/purchase-orders`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase order created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchPOs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save purchase order');
    }
  };

  const handleEdit = (po) => {
    if (po.status !== 'Draft') {
      toast.error('Only draft purchase orders can be edited');
      return;
    }
    setEditingPO(po);
    setFormData({
      supplier_id: po.supplier_id,
      items: po.items,
      notes: po.notes || ''
    });
    setSelectedProducts(po.items);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id, status) => {
    if (status !== 'Draft') {
      toast.error('Only draft purchase orders can be deleted');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this purchase order?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/purchase-orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Purchase order deleted successfully');
      fetchPOs();
    } catch (error) {
      toast.error('Failed to delete purchase order');
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/purchase-orders/${id}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchPOs();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const downloadPDF = async (poId, poNumber) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/purchase-orders/${poId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${poNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '',
      items: [],
      notes: ''
    });
    setSelectedProducts([]);
    setEditingPO(null);
    setProductSearch('');
  };

  const filteredProducts = products.filter(p => 
    (formData.supplier_id === '' || p.supplier_id === formData.supplier_id) &&
    (productSearch === '' || 
     p.product_name.toLowerCase().includes(productSearch.toLowerCase()) ||
     p.sku.toLowerCase().includes(productSearch.toLowerCase()))
  );

  const totals = calculateTotal();

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white">Purchase Orders</h1>
          <p className="text-gray-400 mt-1">Create and manage purchase orders</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700">
              <Plus className="w-4 h-4 mr-2" />
              Create PO
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-900 text-white border-gray-800 max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPO ? 'Edit Purchase Order' : 'Create Purchase Order'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Supplier</Label>
                <Select
                  value={formData.supplier_id}
                  onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}
                  required
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Product Selection */}
              {formData.supplier_id && (
                <div>
                  <Label>Add Products</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search products..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="pl-10 bg-gray-800 border-gray-700"
                    />
                  </div>
                  {productSearch && filteredProducts.length > 0 && (
                    <div className="mt-2 max-h-48 overflow-y-auto bg-gray-800 rounded-md border border-gray-700">
                      {filteredProducts.map((product) => (
                        <div
                          key={product.id}
                          onClick={() => addProduct(product)}
                          className="p-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-0"
                        >
                          <div className="font-medium">{product.product_name}</div>
                          <div className="text-sm text-gray-400">
                            SKU: {product.sku} | ${product.unit_price.toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Selected Products */}
              {selectedProducts.length > 0 && (
                <div>
                  <Label>Selected Products</Label>
                  <div className="space-y-2 mt-2">
                    {selectedProducts.map((item) => (
                      <div key={item.product_id} className="bg-gray-800 p-3 rounded-md flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium">{item.product_name}</div>
                          <div className="text-sm text-gray-400">SKU: {item.sku} | ${item.unit_price.toFixed(2)} each</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <Label className="text-xs">Qty:</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItemQuantity(item.product_id, e.target.value)}
                              className="w-20 bg-gray-700 border-gray-600"
                            />
                          </div>
                          <div className="w-24 text-right font-bold">
                            ${item.total.toFixed(2)}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeProduct(item.product_id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="mt-4 bg-gray-800 p-4 rounded-md space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>${totals.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>GST (10%):</span>
                      <span>${totals.tax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t border-gray-700 pt-2">
                      <span>Total:</span>
                      <span className="text-orange-500">${totals.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-gray-800 border-gray-700"
                  rows={3}
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-orange-600 to-red-600">
                  {editingPO ? 'Update' : 'Create'} PO
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter */}
      <Card className="bg-white/5 border-white/10 mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <Label className="text-white">Status:</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-gray-800 border-gray-700">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Draft">Draft</SelectItem>
                <SelectItem value="Sent">Sent</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
                <SelectItem value="Complete">Complete</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* PO List */}
      <div className="space-y-4">
        {pos.map((po) => (
          <Card key={po.id} className="bg-white/5 border-white/10">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <FileText className="w-6 h-6 text-orange-500" />
                    <div>
                      <CardTitle>{po.po_number}</CardTitle>
                      <p className="text-sm text-gray-400 mt-1">{po.supplier_name}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <Select
                    value={po.status}
                    onValueChange={(value) => updateStatus(po.id, value)}
                  >
                    <SelectTrigger className={`${STATUS_COLORS[po.status]} border-0 h-7 px-3 font-medium text-white hover:opacity-80`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="Draft" className="text-white">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                          Draft
                        </div>
                      </SelectItem>
                      <SelectItem value="Sent" className="text-white">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                          Sent
                        </div>
                      </SelectItem>
                      <SelectItem value="Cancelled" className="text-white">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          Cancelled
                        </div>
                      </SelectItem>
                      <SelectItem value="Complete" className="text-white">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          Complete
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Items:</span>
                  <span className="text-white">{po.items.length} product(s)</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Total:</span>
                  <span className="text-white font-bold">${po.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Created:</span>
                  <span className="text-white">{new Date(po.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    onClick={() => downloadPDF(po.id, po.po_number)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                  {po.status === 'Draft' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(po)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(po.id, po.status)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {pos.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No purchase orders yet. Create your first PO to get started.</p>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrdersPage;
